import React from "react";
import {
  FaAngleLeft,
  FaAngleRight,
  FaAngleDoubleLeft,
  FaAngleDoubleRight,
} from "react-icons/fa";

/**
 * @component Pagination
 * @description A reusable pagination component for handling page navigation and items per page selection.
 *
 * @param {Object} props - Component props
 * @param {number} props.currentPage - The currently active page number
 * @param {number} props.totalPages - The total number of pages available
 * @param {number} props.totalItems - Total number of items in the dataset
 * @param {number} props.itemsPerPage - Number of items displayed per page
 * @param {Function} props.onPageChange - Callback function when the page changes
 * @param {Function} props.onItemsPerPageChange - Callback when the items per page selection changes
 *
 * @returns {JSX.Element} The rendered Pagination component
 */
const Pagination = ({
  currentPage,
  totalPages,
  totalItems,
  itemsPerPage,
  onPageChange,
  onItemsPerPageChange,
  setDirection,
}) => {
  const startItem = (currentPage - 1) * itemsPerPage + 1;
  const endItem = Math.min(currentPage * itemsPerPage, totalItems);

  return (
    <div className="flex items-center justify-between h-[40px] py-1 border-t mt-2 bg-white">
      {/* Results per Page & Count */}
      <div className="flex items-center gap-3">
        <div></div>
        {totalItems > 10 && (
          <div>
            <span className="text-sm text-gray-700">Result per page:</span>
            <select
              value={itemsPerPage}
              onChange={(e) => onItemsPerPageChange(Number(e.target.value))}
              className="border rounded-md px-2 py-1 text-sm"
            >
              {[10, 20, 50, 100].map((size) => (
                <option key={size} value={size}>
                  {size}
                </option>
              ))}
            </select>
          </div>
        )}
        <span className="text-sm text-gray-700">
          {startItem}-{endItem} of {totalItems} results
        </span>
      </div>

      {/* Page Navigation */}
      <div className="flex items-center gap-1">
        {totalItems > 10 && (
          <div>
            <button
              onClick={() => {
                onPageChange(1);
                setDirection?.("backward");
              }}
              disabled={currentPage === 1}
              className={`p-2 ${
                currentPage === 1
                  ? "text-gray-400 "
                  : "text-gray-700 hover:bg-gray-100"
              }`}
            >
              <FaAngleDoubleLeft />
            </button>

            <button
              onClick={() => {
                onPageChange(currentPage - 1);
                setDirection?.("backward");
              }}
              disabled={currentPage === 1}
              className={`p-2 ${
                currentPage === 1
                  ? "text-gray-400 "
                  : "text-gray-700 hover:bg-gray-100"
              }`}
            >
              <FaAngleLeft />
            </button>
          </div>
        )}

        <span className="text-sm text-gray-700">
          Page {currentPage} of {totalPages}
        </span>

        {totalItems > 10 && (
          <div>
            <button
              onClick={() => {
                onPageChange(currentPage + 1);
                setDirection?.("forward");
              }}
              disabled={currentPage === totalPages}
              className={`p-2 ${
                currentPage === totalPages
                  ? "text-gray-400 "
                  : "text-gray-700 hover:bg-gray-100"
              }`}
            >
              <FaAngleRight />
            </button>

            <button
              onClick={() => {
                onPageChange(totalPages);
                setDirection?.("forward");
              }}
              disabled={currentPage === totalPages}
              className={`p-2 ${
                currentPage === totalPages
                  ? "text-gray-400"
                  : "text-gray-700 hover:bg-gray-100"
              }`}
            >
              <FaAngleDoubleRight />
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default Pagination;
